import React from "react";
import { COLORS } from "rylak-common/src/constants";
import styles from "./ReturnStatisticsCard.module.scss";
import {
    getComplaintItems,
    getComplaintItemsWorth,
    getExchangeItems,
    getExchangeItemsFrom,
    getExchangeItemsWorth,
    getGiftCardsCreated,
    getGiftCardsCreatedWorth,
    getPercentageComplaintItems,
    getPercentageExchangeItems,
    getPercentageExchangeItemsFrom,
    getPercentageGiftCards,
    getReturnItemsTurnr,
    getReturnItemsWorth,
} from "~/cores/statisticsCore";
import { formatAmountWithComma, formatAmountWithCurrency } from "~/utils";
import {
    getProductComplaintWorthInPercentage,
    getProductExchangeWorthInPercentage,
    getProductGiftCardWorthInPercentage,
    getProductReturnsInPercentage,
    getProductReturnsWorthInPercentage,
} from "~/cores/productCore";
import { interFont } from "../../../../app/_apps/fonts.jsx";
import classNames from "classnames";
import { RISK_LEVEL_MAP } from "../../../../app/(pages)/customers/RiskLevelChip";
import { getBrandCurrency, isBrandRiskSystemEnabled } from "~/cores/brandCore";
import { getCustomerBrand } from "~/cores/customerCore";
import { chipColorMap } from "~/components/chips/CustomChip.jsx";
import isNil from "lodash/isNil";
import { TurnrTooltip } from "rylak-common/src/components/TurnrTooltip/TurnrTooltip";

const returnColor = COLORS.RETURN;
const exchangeColor = COLORS.EXCHANGE;
const complaintColor = COLORS.COMPLAINT;
const giftCardColor = COLORS.GIFT_CARD;

function TooltipThirdColumn({ leftText, percentage, text }) {
    return (
        <>
            <div
                style={{
                    // color: color,
                    marginLeft: "10px",
                    fontSize: "12px",
                }}
            >
                {leftText}
                {!isNil(percentage) && Math.round(percentage * 1000) / 10 + "%"}
            </div>
            <div
                style={{
                    color: COLORS.WEAK_TEXT,
                    fontSize: "12px",
                    marginLeft: "-5px",
                    whiteSpace: "nowrap",
                }}
            >
                {text}
            </div>
        </>
    );
}

function WorthColumn({ worth, currencyCode }) {
    return (
        <div
            style={{
                // color: color,
                marginLeft: "auto",
                fontSize: "12px",
            }}
        >
            {formatAmountWithCurrency(worth, { currencyCode })}
        </div>
    );
}

function ColoredBox({ color, text, style, textColor, bold = true }) {
    return (
        <div
            style={{
                ...style,
                backgroundColor: color,
                textAlign: "center",
                fontFamily: interFont.style.fontFamily,
                fontSize: "12px",
                fontWeight: bold && "bold",
                color: textColor || "white",
                borderRadius: "2px",
                padding: "5px 8px",
            }}
        >
            {text}
        </div>
    );
}

export default function ReturnStatisticsCard({ customer, statistics = customer?.statistics, currencyCode = getBrandCurrency(getCustomerBrand(customer)), showValue = true }) {
    return (
        <div className={classNames(styles["grid-container"], !showValue && styles["grid-container-no-value"])}>
            {/*<div className={styles["grid-item"]}>Ordered items:</div>*/}
            {/*<ColoredBox text={formatAmountWithComma(getOrderItems(statistics))} color={orderColor} />*/}
            {/*<div></div>*/}
            {/*<div></div>*/}
            {/*<WorthColumn color={orderColor} worth={getOrdersWorth(statistics)} text={"of 13 ordered items"} />*/}

            <div className={styles["grid-item"]}>Return items:</div>
            <ColoredBox text={formatAmountWithComma(getReturnItemsTurnr(statistics))} color={returnColor} />
            <TooltipThirdColumn color={returnColor} percentage={getProductReturnsInPercentage(statistics)} text={"of ordered"} />
            {showValue && <TooltipThirdColumn color={returnColor} percentage={getProductReturnsWorthInPercentage(statistics)} text={"of order value"} />}
            {showValue && <WorthColumn color={returnColor} worth={getReturnItemsWorth(statistics)} currencyCode={currencyCode} />}

            <div className={styles["grid-item"]}>Exchanges:</div>
            <ColoredBox text={formatAmountWithComma(getExchangeItemsFrom(statistics) || getExchangeItems(statistics))} color={exchangeColor} />
            <TooltipThirdColumn color={exchangeColor} percentage={getPercentageExchangeItemsFrom(statistics) || getPercentageExchangeItems(statistics)} text={"of returns"} />
            {showValue && <TooltipThirdColumn color={returnColor} percentage={getProductExchangeWorthInPercentage(statistics)} text={"of return value"} />}
            {showValue && <WorthColumn color={exchangeColor} worth={getExchangeItemsWorth(statistics)} currencyCode={currencyCode} />}

            <div className={styles["grid-item"]}>Complaints:</div>
            <ColoredBox text={formatAmountWithComma(getComplaintItems(statistics))} color={complaintColor} />
            <TooltipThirdColumn color={complaintColor} percentage={getPercentageComplaintItems(statistics)} text={"of returns"} />
            {showValue && <TooltipThirdColumn color={returnColor} percentage={getProductComplaintWorthInPercentage(statistics)} text={"of return value"} />}
            {showValue && <WorthColumn color={complaintColor} worth={getComplaintItemsWorth(statistics)} currencyCode={currencyCode} />}

            {getGiftCardsCreated(statistics) > 0 && (
                <>
                    <div className={styles["grid-item"]}>Giftcards:</div>
                    <ColoredBox text={formatAmountWithComma(getGiftCardsCreated(statistics))} color={giftCardColor} />
                    <TooltipThirdColumn color={giftCardColor} percentage={getPercentageGiftCards(statistics)} text={"of refunds"} />
                    {showValue && <TooltipThirdColumn color={returnColor} percentage={getProductGiftCardWorthInPercentage(statistics)} text={"of refund value"} />}
                    {showValue && <WorthColumn color={giftCardColor} worth={getGiftCardsCreatedWorth(statistics)} currencyCode={currencyCode} />}
                </>
            )}
            {isBrandRiskSystemEnabled(getCustomerBrand(customer)) && (
                <>
                    <div className={styles["grid-item"]}>Risk level:</div>
                    <TurnrTooltip content={RISK_LEVEL_MAP[customer?.riskLevel]?.tooltipTitleFn(customer)}>
                        <ColoredBox
                            text={RISK_LEVEL_MAP[customer?.riskLevel]?.label}
                            color={chipColorMap[RISK_LEVEL_MAP[customer?.riskLevel]?.colorType]?.secondary}
                            textColor={chipColorMap[RISK_LEVEL_MAP[customer?.riskLevel]?.colorType]?.dark}
                            bold={false}
                        />
                    </TurnrTooltip>
                    <TooltipThirdColumn color={giftCardColor} leftText={Math.round(customer?.riskScore)} />
                </>
            )}
        </div>
    );
}
